import React, { type FC, type MutableRefObject } from 'react';
import { type NavigateFunction, useNavigate } from '@leagueplatform/routing';
import { useIntl, type UseIntlReturn } from '@leagueplatform/locales';
import { MasonryEngine } from './masonry-engine.component';
import { type MasonryEngineDriver } from './types/masonry-engine-driver.types';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeAction,
} from './types/masonry-engine-node.types';
import { type InvokeActionFn } from './types/masonry-engine-action.types';
import { createInstanceResources } from './create-instance-resources';
import { createInvokeActionFn } from './masonry-engine-action-controller';

/**
 * An object containing a renderable Masonry instance component, and a
 * `invokeAction` function bound to that instance which can be used to
 * invoke actions on it.
 */
type MasonryEngineInstance<KnownActions extends MasonryEngineNodeAction> = {
  Component: FC<{ rootNode: MasonryEngineNode }>;
  invokeAction: InvokeActionFn<KnownActions, true>;
};

/**
 * Creates a {@link MasonryEngineInstance `MasonryEngineInstance`}.
 */
export const createMasonryEngineInstance = <
  Node extends MasonryEngineNode,
  KnownActions extends MasonryEngineNodeAction,
>(
  driver: MasonryEngineDriver<Node, KnownActions, any>,
): MasonryEngineInstance<KnownActions> => {
  const instanceResources = createInstanceResources();

  const navigateFnRef: MutableRefObject<NavigateFunction | undefined> = {
    current: undefined,
  };

  const intlRef: MutableRefObject<UseIntlReturn | undefined> = {
    current: undefined,
  };

  /**
   * We pass empty navigate and intl refs to the factory because we know that,
   * below, we'll only allow calling its return when those refs are eventually
   * non-empty.
   */
  const invokeActionFn = createInvokeActionFn({
    actionController: driver.actionController,
    instanceResources,
    navigateFnRef: navigateFnRef as MutableRefObject<NavigateFunction>,
    intlRef: intlRef as MutableRefObject<UseIntlReturn>,
    ancestry: [],
  });

  const Component = ({ rootNode }: { rootNode: MasonryEngineNode }) => {
    const navigate = useNavigate();

    navigateFnRef.current = navigate;

    const intl = useIntl();
    intlRef.current = intl;

    return (
      <MasonryEngine
        driver={driver}
        rootNode={rootNode}
        instanceResources={instanceResources}
      />
    );
  };
  return {
    Component,
    invokeAction: (...args) => {
      if (!intlRef.current || !navigateFnRef.current) {
        throw new Error(
          'Attempted to invoke an action before the MasonryEngine instance was mounted.',
        );
      }

      return invokeActionFn(...args);
    },
  };
};
